import React, {ChangeEvent, useEffect} from "react";

import {
  IonList,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar,
  IonIcon,
} from "@ionic/react";
import {ErrorMessage} from "@hookform/error-message";
import "../Select/select.styles.css";

interface IOption {
  value: string;
  display: string;
}
interface ISelect {
  register: any;
  errors: any;
  registerName: string;
  name: string;
  pattern?: {
    value: RegExp | string;
    message: string;
  };
  requiredMessage?: string | false;
  options: IOption[];
  multiple?: boolean;
  validate?: any;
  disabled?: boolean;

  hasSearch?: boolean;
  setValue: any;
  getValues: any;
  watch: any;

  clearErrors: any;

  required?: boolean;
}

// Constants for performance optimization
const INITIAL_ITEMS = 50;
const LOAD_MORE_ITEMS = 30;
const SEARCH_DEBOUNCE_DELAY = 300;

const SelectWithSearch = ({
  errors,
  registerName,
  name,
  options,
  hasSearch,
  setValue,
  watch,
  clearErrors,
  required,
  register,
}: ISelect) => {
  const [search, setSearch] = React.useState("");
  const [selectOptions, setSelectOptions] = React.useState<IOption[]>([]);
  const [visibleItems, setVisibleItems] = React.useState(INITIAL_ITEMS);
  const [defaultValue, setDefaultValue] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const contentRef = React.useRef<HTMLIonContentElement>(null);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>();
  const watchedValue = watch(registerName);

  const filterOptions = React.useCallback(
    (searchTerm: string, items: IOption[]) => {
      if (!searchTerm) return items;
      const term = searchTerm.toLowerCase();
      return items.filter((item) => item.display.toLowerCase().includes(term));
    },
    []
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!hasSearch) return;

    const searchValue = e.target.value;
    setSearch(searchValue);

    // Debounce the search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const filtered = searchValue
        ? filterOptions(searchValue, options || [])
        : options || [];

      setSelectOptions(filtered);
      setVisibleItems(INITIAL_ITEMS);
      clearErrors?.(registerName);
    }, SEARCH_DEBOUNCE_DELAY);
  };

  const handleSelectItem = (item: IOption) => {
    setValue(registerName, item.value, {shouldValidate: true});
    clearErrors?.(registerName);
    setIsOpen(false);
    setSearch("");
    setVisibleItems(INITIAL_ITEMS);
  };

  const handleScroll = (e: CustomEvent) => {
    if (isLoading || visibleItems >= selectOptions.length) return;

    const scrollElement = e.target as HTMLElement;
    const {scrollTop, scrollHeight, clientHeight} = scrollElement;

    // Load more when near the bottom
    if (scrollHeight - (scrollTop + clientHeight) < 100) {
      setIsLoading(true);
      // Simulate loading delay
      setTimeout(() => {
        setVisibleItems((prev) =>
          Math.min(prev + LOAD_MORE_ITEMS, selectOptions.length)
        );
        setIsLoading(false);
      }, 100);
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setVisibleItems(INITIAL_ITEMS);
      setSelectOptions(options || []);
    } else {
      setSearch("");
      setIsLoading(false);
    }
  }, [isOpen, options]);

  useEffect(() => {
    const value = options?.find((item) => item.value === watchedValue);
    setDefaultValue(value?.display ?? "");
  }, [watchedValue, options]);

  const visibleOptions = React.useMemo(() => {
    return selectOptions.slice(0, visibleItems);
  }, [selectOptions, visibleItems]);

  return (
    <div className="app-input-wrapper">
      <div className="input-label">
        <IonLabel className="label-text">{name}</IonLabel>
        {required && <span>*</span>}
      </div>

      <div className={`app-input-wrapper`}>
        <IonInput
          style={{
            border: "1px solid #a2a2a2",
            height: "50px",
            borderRadius: "5px",
          }}
          placeholder={name}
          value={defaultValue}
          onClick={() => setIsOpen(true)}
          {...register(registerName, {
            required: required ? `${name} è obbligatorio` : false,
          })}
        />
      </div>

      {isOpen && (
        <IonModal isOpen={true}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{name}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsOpen(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent
            className="ion-padding"
            scrollEvents={true}
            onIonScroll={handleScroll}
            ref={contentRef}
            style={{
              "--padding-bottom": "16px",
              "--padding-end": "16px",
              "--padding-start": "16px",
              "--padding-top": "16px",
              "--ion-padding": "16px",
            }}
          >
            {hasSearch && (
              <div className="searchable-select-input">
                <input
                  type="text"
                  id="search-text"
                  placeholder="Cerca..."
                  value={search}
                  onChange={handleSearch}
                  style={{
                    border: "1px solid grey",
                    width: "100%",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    marginBottom: "8px",
                  }}
                  autoFocus
                />
                <IonIcon name="search-outline" className="input-search-icon" />
              </div>
            )}

            <IonList className="select-list">
              {visibleOptions.length > 0 ? (
                visibleOptions.map((item) => (
                  <IonItem
                    key={item.value}
                    className="select-item"
                    button
                    onClick={() => handleSelectItem(item)}
                  >
                    <IonLabel>{item.display}</IonLabel>
                  </IonItem>
                ))
              ) : (
                <IonItem>
                  <IonLabel className="ion-text-center">
                    Nessun risultato trovato
                  </IonLabel>
                </IonItem>
              )}
              {isLoading && (
                <IonItem>
                  <IonLabel className="ion-text-center">
                    Caricamento...
                  </IonLabel>
                </IonItem>
              )}
            </IonList>
          </IonContent>
        </IonModal>
      )}

      <ErrorMessage errors={errors} name={registerName} as={"span"} />
    </div>
  );
};

export default SelectWithSearch;
